import Observer from "../../utils/observer";
import type FilterableFileList from "./FilterableFileList";
import type { FileListObserverPayload } from "./FilterableFileList";

export type MobileFilterOverlayLabels = {
  open: string;
  apply: string;
  close: string;
};

export type MobileFilterOverlayOptions = {
  // Selector (within the container) of the element that becomes the bottom-sheet.
  sheetSelector: string;
  labels: MobileFilterOverlayLabels;
  // When provided, closing via X/backdrop reverts changes made while the sheet
  // was open; only "Apply" commits them.
  fileList?: FilterableFileList;
};

type FilterSnapshot = { type: string; active: string[] };

const FILTER_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="20" viewBox="0 0 22 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><line x1="2" y1="5" x2="20" y2="5"/><line x1="2" y1="10" x2="20" y2="10"/><line x1="2" y1="15" x2="20" y2="15"/><circle cx="15" cy="5" r="2.4" fill="currentColor" stroke="none"/><circle cx="7" cy="10" r="2.4" fill="currentColor" stroke="none"/><circle cx="16" cy="15" r="2.4" fill="currentColor" stroke="none"/></svg>`;
const CLOSE_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"><line x1="2" y1="2" x2="12" y2="12"/><line x1="12" y1="2" x2="2" y2="12"/></svg>`;

// Unique id per instance to link the sheet to its heading via aria-labelledby.
let dialogIdCounter = 0;

/**
 * Presentation layer that turns an existing filter block into a mobile
 * bottom-sheet behind a floating trigger button. Filtering itself stays in
 * FilterableFileList; this class only manages the overlay DOM, reflects the
 * active-filter count via an observer, and (optionally) reverts uncommitted
 * changes when the sheet is dismissed without applying.
 */
export default class MobileFilterOverlay {
  private readonly container: HTMLElement;

  private readonly sheet: HTMLElement;

  private readonly labels: MobileFilterOverlayLabels;

  private readonly fileList?: FilterableFileList;

  private snapshot: FilterSnapshot[] | null = null;

  private built = false;

  private toggleButton!: HTMLButtonElement;

  private badge!: HTMLElement;

  private backdrop!: HTMLElement;

  private handleKeydown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      this.cancel();
    } else if (event.key === "Tab") {
      this.trapFocus(event);
    }
  };

  constructor(container: HTMLElement, options: MobileFilterOverlayOptions) {
    const sheet = container.querySelector<HTMLElement>(options.sheetSelector);
    if (sheet === null) {
      throw new Error(
        `MobileFilterOverlay: no element matching "${options.sheetSelector}" found.`
      );
    }
    this.container = container;
    this.sheet = sheet;
    this.labels = options.labels;
    this.fileList = options.fileList;
    // No trigger/sheet on pages without selectable filters (only the "show all"
    // default, data-filter="*", or nothing at all).
    if (this.hasSelectableFilters()) {
      this.build();
      this.built = true;
    }
  }

  private hasSelectableFilters(): boolean {
    return (
      this.sheet.querySelectorAll('[data-filter]:not([data-filter="*"])')
        .length > 0
    );
  }

  private build() {
    this.sheet.classList.add("mobile-filter-sheet");

    dialogIdCounter += 1;
    const titleId = `mobile-filter-title-${dialogIdCounter}`;
    this.sheet.setAttribute("role", "dialog");
    this.sheet.setAttribute("aria-modal", "true");
    this.sheet.setAttribute("aria-labelledby", titleId);
    this.sheet.tabIndex = -1;

    const header = document.createElement("div");
    header.className = "mobile-filter-sheet__header";

    const title = document.createElement("span");
    title.id = titleId;
    title.className = "mobile-filter-sheet__title";
    title.innerHTML = `${FILTER_ICON}<span>${this.labels.open}</span>`;

    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.className = "mobile-filter-sheet__close";
    closeButton.setAttribute("aria-label", this.labels.close);
    closeButton.innerHTML = CLOSE_ICON;
    closeButton.addEventListener("click", () => this.cancel());

    header.append(title, closeButton);
    this.sheet.prepend(header);

    const footer = document.createElement("div");
    footer.className = "mobile-filter-sheet__footer";

    const applyButton = document.createElement("button");
    applyButton.type = "button";
    applyButton.className = "btn btn-primary rounded-pill w-50";
    applyButton.textContent = this.labels.apply;
    applyButton.addEventListener("click", () => this.apply());

    footer.append(applyButton);
    this.sheet.append(footer);

    this.backdrop = document.createElement("div");
    this.backdrop.className = "mobile-filter-backdrop";
    this.backdrop.setAttribute("aria-hidden", "true");
    this.backdrop.addEventListener("click", () => this.cancel());
    this.container.append(this.backdrop);

    this.badge = document.createElement("span");
    this.badge.className = "mobile-filter-toggle__badge";
    this.badge.hidden = true;

    this.toggleButton = document.createElement("button");
    this.toggleButton.type = "button";
    this.toggleButton.className = "mobile-filter-toggle";
    this.toggleButton.setAttribute("aria-expanded", "false");
    this.toggleButton.setAttribute("aria-label", this.labels.open);
    this.toggleButton.innerHTML = FILTER_ICON;
    this.toggleButton.append(this.badge);
    this.toggleButton.addEventListener("click", () => this.open());
    this.container.append(this.toggleButton);
  }

  private open() {
    this.captureState();
    this.sheet.classList.add("is-open");
    this.backdrop.classList.add("is-open");
    this.toggleButton.setAttribute("aria-expanded", "true");
    document.body.classList.add("mobile-filter-open");
    document.addEventListener("keydown", this.handleKeydown);
    this.sheet.focus();
  }

  private apply() {
    this.hide();
  }

  private cancel() {
    this.restoreState();
    this.hide();
  }

  private hide() {
    this.sheet.classList.remove("is-open");
    this.backdrop.classList.remove("is-open");
    this.toggleButton.setAttribute("aria-expanded", "false");
    document.body.classList.remove("mobile-filter-open");
    document.removeEventListener("keydown", this.handleKeydown);
    this.toggleButton.focus();
  }

  private getFocusableElements(): HTMLElement[] {
    return Array.from(
      this.sheet.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    );
  }

  private trapFocus(event: KeyboardEvent) {
    const focusable = this.getFocusableElements();
    if (focusable.length === 0) {
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === this.sheet)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  private captureState() {
    if (this.fileList === undefined) {
      this.snapshot = null;
      return;
    }
    this.snapshot = this.fileList.filterSets.map((filterSet) => ({
      type: filterSet.getType(),
      active: filterSet
        .getActiveFilters()
        .filter((filter) => !filter.isDefault())
        .map((filter) => filter.identifier),
    }));
  }

  private restoreState() {
    if (this.fileList === undefined || this.snapshot === null) {
      return;
    }
    this.fileList.reset();
    this.snapshot.forEach(({ type, active }) => {
      active.forEach((identifier) => {
        this.fileList?.setFilterState(type, identifier, true, true);
      });
    });
  }

  private updateCount(count: number) {
    if (!this.built) {
      return;
    }
    if (count > 0) {
      this.badge.textContent = String(count);
      this.badge.hidden = false;
    } else {
      this.badge.hidden = true;
    }
  }

  // Observer to attach to FilterableFileList.init(); keeps the badge in sync.
  // The "show all" default filter is excluded from the count.
  createCountObserver(): Observer<FileListObserverPayload> {
    return new Observer<FileListObserverPayload>(({ activeFilters }) => {
      const count = activeFilters.filter(
        (filter) => !filter.isDefault()
      ).length;
      this.updateCount(count);
    });
  }
}
